/**
 * ======================================================================
 * UPSELL MODAL COMPONENT - Post-cart upsell modal functionality
 * ======================================================================
 * 
 * This component creates a modal popup that appears after adding items
 * to cart, showing personalized product recommendations.
 * 
 * Key Features:
 * - Event-based cart monitoring (renderful:addToCartSuccess + cart:update)
 * - Fetches product data from custom.modal_upsells metafield
 * - Respects fallback behavior settings (show/hide when no upsells)
 * - Mobile-responsive design with proper sizing
 * - "Personalise" buttons link to product pages (not add to cart)
 * 
 * Files involved:
 * - snippets/upsell-modal.liquid (HTML structure & CSS)
 * - sections/modal-suggestions.liquid (server-side data provider)
 * - This file (JavaScript controller)
 * ======================================================================
 */

import { Component } from '@theme/component';

export class UpsellModalComponent extends Component {
  /**
   * Initialize the modal component when connected to DOM
   * Sets up fetch interception, event listeners, and modal elements
   */
  connectedCallback() {
    super.connectedCallback();
    
    // Get essential DOM elements
    this.dialog = this.querySelector('dialog');
    this.suggestionsContainer = this.querySelector('#upsell-modal__suggestions');
    
    if (!this.dialog || !this.suggestionsContainer) {
      return;
    }

    // No session blocking - modal can show every time
    this.modalShown = false;
    
    // Mark modal as shown when personalise button is clicked (redirect approach)
    // Session tracking prevents modal from showing again after user personalizes

    // Set up event-based cart monitoring
    const self = this;
    
    // Listen for Renderful add to cart success
    document.addEventListener('renderful:addToCartSuccess', function(e) {
      console.log('🎯 Renderful add to cart detected:', e.detail);
      
      if (window.UpsellModalConfig && window.UpsellModalConfig.enabled) {
        const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (productHandle) {
          // Small delay to ensure cart is fully updated
          setTimeout(() => {
            self.showTestModal(productHandle, true);
          }, 500);
        }
      }
    });
    
    // Listen for non-Renderful add to cart (from product forms)
    document.addEventListener('cart:update', function(e) {
      // Only trigger for successful product form submissions
      if (e.detail?.data?.source === 'product-form-component' && 
          !e.detail?.data?.didError) {
        
        console.log('🛒 Product form add to cart detected:', e.detail);
        
        if (window.UpsellModalConfig && window.UpsellModalConfig.enabled) {
          const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
          if (productHandle) {
            // Check for recent product upsell additions to avoid conflicts
            if (window.lastCartAddTime && (Date.now() - window.lastCartAddTime) < 3000) {
              console.log('⏭️ Skipping modal - recent product upsell addition detected');
              return;
            }
            
            // Small delay to ensure cart is fully updated
            setTimeout(() => {
              self.showTestModal(productHandle);
            }, 500);
          }
        }
      }
    });

    // Close modal when clicking backdrop
    this.addEventListener('click', function(event) {
      if (event.target === self.dialog) {
        self.close();
      }
    });

    // Close modal on Escape key
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && self.dialog.open) {
        self.close();
      }
    });
  }

  /**
   * Fetch upsell suggestions and display modal if products are available
   * @param {string} productHandle - The product handle to get suggestions for
   * @param {boolean} forceShow - Force show modal even if current product in cart
   */
  showTestModal(productHandle, forceShow = false) {
    
    const self = this;
    // Use Section Rendering API to get product data without JSON parsing issues
    let url = '/products/' + productHandle + '?section_id=modal-suggestions&t=' + Date.now();
    if (forceShow) {
      url += '&force_show=true';
    }
    
    fetch(url).then(function(response) {
      return response.text();
    }).then(function(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const dataElement = doc.querySelector('[data-suggestions-data]');
      
      if (dataElement) {
        const suggestionsFound = dataElement.getAttribute('data-suggestions-found') === 'true';
        
        if (suggestionsFound) {
          // Get product data from data attributes
          const productDivs = doc.querySelectorAll('.modal-product');
          let html = '';
          
          for (let i = 0; i < productDivs.length; i++) {
            const productDiv = productDivs[i];
            const title = productDiv.getAttribute('data-title');
            const handle = productDiv.getAttribute('data-handle');
            const price = productDiv.getAttribute('data-price');
            const url = productDiv.getAttribute('data-url');
            const imageUrl = productDiv.getAttribute('data-image');
            const productId = productDiv.getAttribute('data-id');
            const variantId = productDiv.getAttribute('data-variant-id');
            
            if (title && price && productId) {
              html += '<div class="upsell-suggestion-card">';
              
              // Image with title (for mobile)
              if (imageUrl) {
                html += '<div class="upsell-card-image" data-title="' + title + '">';
                html += '<img src="' + imageUrl + '" alt="' + title + '" width="60" height="60">';
                html += '<h4 class="upsell-card-title-mobile">' + title + '</h4>';
                html += '</div>';
              }
              
              // Product details (desktop title + price)
              html += '<div class="upsell-card-details">';
              html += '<h4 class="upsell-card-title">' + title + '</h4>';
              html += '<p class="upsell-card-price">' + price + '</p>';
              html += '</div>';
              
              // Button
              html += '<div class="upsell-card-action">';
              // Create redirect to product page with auto-launch parameter
              let productUrl = url + '?personalise=auto';
              if (variantId) {
                productUrl += '&variant=' + variantId;
              }
              html += '<button onclick="window.location.href=\'' + productUrl + '\'" class="button button--primary upsell-modal-personalise-btn">Personalise</button>';
              html += '</div>';
              
              html += '</div>';
            }
          }
          
          if (html) {
            // Only show modal if we have valid products to display
            self.suggestionsContainer.innerHTML = html;
            self.dialog.style.opacity = ''; // Remove initial opacity
            self.dialog.showModal();
            self.style.display = 'block';
          } else {
            // No valid products found - check theme setting for fallback behavior
            if (window.UpsellModalConfig && window.UpsellModalConfig.fallbackBehavior === 'none') {
              return; // User chose "don't show modal" - exit silently
            }
            // User chose "show anyway" - display empty state message
            self.suggestionsContainer.innerHTML = '<p style="text-align: center; padding: 2rem;">No recommendations available.</p>';
            self.dialog.style.opacity = ''; // Remove initial opacity
            self.dialog.showModal();
            self.style.display = 'block';
          }
        } else {
          // No upsell metafield data found - check theme setting for fallback behavior  
          if (window.UpsellModalConfig && window.UpsellModalConfig.fallbackBehavior === 'none') {
            return; // User chose "don't show modal" - exit silently
          }
          // User chose "show anyway" - display empty state message
          self.suggestionsContainer.innerHTML = '<p style="text-align: center; padding: 2rem;">No recommendations available.</p>';
          self.dialog.style.opacity = ''; // Remove initial opacity
          self.dialog.showModal();
          self.style.display = 'block';
        }
      } else {
        self.suggestionsContainer.innerHTML = '<p style="text-align: center; padding: 2rem;">Error loading recommendations.</p>';
        self.dialog.style.opacity = ''; // Remove initial opacity
        self.dialog.showModal();
        self.style.display = 'block';
      }
    });
  }

  close() {
    if (this.dialog.open) {
      this.dialog.close();
      this.style.display = 'none';
    }
  }

  show() {
    const productHandle = window.location.pathname.split('/products/')[1].split('?')[0];
    self.showTestModal(productHandle);
  }
}

customElements.define('upsell-modal-component', UpsellModalComponent);

window.UpsellModal = {
  show: function() {
    const modal = document.querySelector('upsell-modal-component');
    if (modal) {
      modal.show();
    }
  },
  close: function() {
    const modal = document.querySelector('upsell-modal-component');
    if (modal) {
      modal.close();
    }
  }
};